import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PolicyAiService, PolicyPromptOptions, PolicySuggestion } from '../../../../services/policy-ai.service';
import { CommonModule } from '@angular/common';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

export interface PolicyAiDialogData {
    existingPolicy?: string;
    companyName?: string;
    // Selection-based refinement
    selectedText?: string;
    fullDocument?: string;
}

/**
 * Result returned from the AI dialog
 */
export interface PolicyAiDialogResult {
    content: string;
    isSelectionRefinement: boolean;
}

@Component({
    selector: 'shared-policy-ai-dialog',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatSnackBarModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCardModule,
        MatProgressSpinnerModule,
        EditorModule,
    ],
    providers: [PolicyAiService, { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }],
    template: `
        <h2 mat-dialog-title>
            AI Policy Assistant
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="ai-dialog-content">
            <form [formGroup]="form">
                <!-- API Key Configuration (if not set) -->
                @if (!hasApiKey) {
                    <mat-card class="api-key-card">
                        <mat-card-header>
                            <mat-card-title>Setup Required</mat-card-title>
                        </mat-card-header>
                        <mat-card-content>
                            <p>To use AI assistance, please provide your Google Gemini API key:</p>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Gemini API Key</mat-label>
                                <input
                                    matInput
                                    type="password"
                                    formControlName="apiKey"
                                    placeholder="Enter your API key"
                                />
                                <mat-hint
                                    >Get your free API key at
                                    <a href="https://aistudio.google.com" target="_blank">Google AI Studio</a></mat-hint
                                >
                            </mat-form-field>
                            <div style="display: flex; justify-content: space-between; align-items: center;">
                                <button
                                    mat-raised-button
                                    color="primary"
                                    (click)="setApiKey()"
                                    [disabled]="form.get('apiKey')?.invalid"
                                >
                                    Save API Key
                                </button>
                                <small style="color: #666;">
                                    <mat-icon style="font-size: 16px; height: 16px; width: 16px;">info</mat-icon>
                                    Stored securely in your browser
                                </small>
                            </div>
                        </mat-card-content>
                    </mat-card>
                }

                <!-- AI Options (if API key is set) -->
                @if (hasApiKey) {
                    <!-- Selection Mode Banner -->
                    @if (isSelectionMode) {
                        <div class="selection-mode-banner">
                            <mat-icon class="quote-icon">format_quote</mat-icon>
                            <div class="selection-info">
                                <div class="selection-header">Refining Selected Text</div>
                                <div class="selection-preview">"{{ getSelectionPreview() }}"</div>
                            </div>
                            <button
                                mat-icon-button
                                class="clear-btn"
                                (click)="clearSelection()"
                                matTooltip="Clear selection and refine full document"
                            >
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>
                    }

                    <!-- Action Type Selection -->
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>What would you like to do?</mat-label>
                        <mat-select formControlName="actionType">
                            @if (isSelectionMode) {
                                <mat-option value="refine_selection">Refine selected text</mat-option>
                                <mat-option value="improve">Improve selected text</mat-option>
                                <mat-option value="concise">Make selection more concise</mat-option>
                                <mat-option value="expand">Expand selection with detail</mat-option>
                                <mat-option value="polish">Polish selected text</mat-option>
                                <mat-option value="grammar">Fix grammar in selection</mat-option>
                            } @else if (data.existingPolicy || generatedHtmlContent) {
                                <mat-option value="improve">Improve existing policy</mat-option>
                                <mat-option value="complete">Complete partial policy</mat-option>
                                <mat-option value="summarize">Summarize policy</mat-option>
                                <mat-option value="concise">Make more concise</mat-option>
                                <mat-option value="expand">Expand with more detail</mat-option>
                                <mat-option value="polish">Polish and refine</mat-option>
                                <mat-option value="grammar">Fix grammar and spelling</mat-option>
                            }
                            <mat-option value="template">Create new policy template</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <!-- Refinement indicator -->
                    @if (!isSelectionMode && generatedHtmlContent && form.get('actionType')?.value !== 'template') {
                        <div class="refinement-notice">
                            <mat-icon>refresh</mat-icon>
                            <span>Will refine the generated content</span>
                        </div>
                    }

                    <!-- Template Type (for new templates) -->
                    @if (form.get('actionType')?.value === 'template') {
                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Policy Type</mat-label>
                            <mat-select formControlName="templateType">
                                @for (template of availableTemplates; track template.type) {
                                    <mat-option [value]="template.type">
                                        <strong>{{ template.label }}</strong>
                                        <br />
                                        <small>{{ template.description }}</small>
                                    </mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    }

                    <!-- Context/Instructions -->
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Additional Context (Optional)</mat-label>
                        <textarea
                            matInput
                            formControlName="context"
                            cdkTextareaAutosize
                            cdkAutosizeMinRows="2"
                            cdkAutosizeMaxRows="4"
                            placeholder="Provide any specific requirements, industry context, or special considerations..."
                        ></textarea>
                    </mat-form-field>

                    <!-- Tone Selection -->
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Writing Tone</mat-label>
                        <mat-select formControlName="tone">
                            <mat-option value="professional">Professional</mat-option>
                            <mat-option value="formal">Formal</mat-option>
                            <mat-option value="friendly">Friendly</mat-option>
                            <mat-option value="casual">Casual</mat-option>
                            <mat-option value="authoritative">Authoritative</mat-option>
                            <mat-option value="empathetic">Empathetic</mat-option>
                            <mat-option value="simple">Simple / Plain Language</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <!-- Generate Button -->
                    <div class="action-buttons">
                        <button
                            mat-raised-button
                            color="primary"
                            (click)="generateContent()"
                            [disabled]="!canGenerate() || isLoading"
                            class="generate-btn"
                        >
                            @if (isLoading) {
                                <ng-container>
                                    <mat-spinner diameter="20"></mat-spinner>
                                    <span>{{ isRefiningContent() ? 'Refining...' : 'Generating...' }}</span>
                                </ng-container>
                            } @else {
                                <ng-container>
                                    <mat-icon>{{ isRefiningContent() ? 'auto_fix_high' : 'auto_awesome' }}</mat-icon>
                                    <span>{{ isRefiningContent() ? 'Refine Content' : 'Generate with AI' }}</span>
                                </ng-container>
                            }
                        </button>
                    </div>

                    <!-- Generated Content -->
                    @if (generatedHtmlContent) {
                        <mat-card class="suggestion-card">
                            <mat-card-header>
                                <mat-card-title>{{
                                    hasBeenRefined ? 'AI Generated Content' : 'Current Policy Content'
                                }}</mat-card-title>
                                <mat-card-subtitle *ngIf="hasBeenRefined">
                                    Confidence: {{ (generatedSuggestion?.confidence || 0) * 100 | number: '1.0-0' }}%
                                </mat-card-subtitle>
                            </mat-card-header>
                            <mat-card-content>
                                <div class="generated-content-editor">
                                    <editor
                                        [init]="tinymcePreviewConfig"
                                        [initialValue]="generatedHtmlContent"
                                        (onInit)="onPreviewEditorInit($event)"
                                    >
                                    </editor>
                                </div>
                            </mat-card-content>
                            <mat-card-actions>
                                <button mat-button (click)="copyToClipboard()">
                                    <mat-icon>content_copy</mat-icon>
                                    Copy
                                </button>
                                <button mat-raised-button color="primary" (click)="useContent()">
                                    <mat-icon>check</mat-icon>
                                    Use This Content
                                </button>
                            </mat-card-actions>
                        </mat-card>
                    }

                    <!-- Error Display -->
                    @if (errorMessage) {
                        <mat-card class="error-card">
                            <mat-card-content>
                                <mat-icon color="warn">error</mat-icon>
                                <span class="ml-2">{{ errorMessage }}</span>
                            </mat-card-content>
                        </mat-card>
                    }
                }
            </form>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Cancel</button>
            @if (generatedHtmlContent) {
                <button mat-raised-button color="primary" (click)="useContent()">
                    <mat-icon>check</mat-icon>
                    Use Content
                </button>
            }
        </mat-dialog-actions>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .ai-dialog-content {
                padding: 24px 24px 0 24px;
                overflow: visible !important;
            }

            :host ::ng-deep .mat-mdc-dialog-content {
                overflow: visible !important;
            }

            .api-status-inline {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                padding: 8px 12px;
                background: rgba(76, 175, 80, 0.1);
                border-radius: 4px;
                font-size: 13px;
            }

            .api-status-inline mat-icon {
                font-size: 18px;
                height: 18px;
                width: 18px;
            }

            .api-status-inline span {
                flex: 1;
                color: #4caf50;
            }

            .clear-key-btn {
                padding: 0 8px !important;
                min-width: unset !important;
                line-height: 28px !important;
                font-size: 12px !important;
            }

            .clear-key-btn mat-icon {
                font-size: 14px;
                height: 14px;
                width: 14px;
                margin-right: 4px;
            }

            .action-buttons {
                margin: 16px 0;
                text-align: center;
            }

            .refinement-notice {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                margin-top: -8px;
                margin-bottom: 8px;
                background: rgba(255, 193, 7, 0.1);
                border: 1px solid rgba(255, 193, 7, 0.3);
                border-radius: 4px;
                font-size: 12px;
                color: #ffc107;
            }

            .refinement-notice mat-icon {
                font-size: 16px;
                height: 16px;
                width: 16px;
            }

            .generate-btn.mat-mdc-raised-button {
                padding-left: 12px;
                padding-right: 16px;
            }

            .generate-btn.mat-mdc-raised-button ::ng-deep .mdc-button__label {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }

            .generate-btn.mat-mdc-raised-button mat-icon {
                font-size: 18px;
                height: 18px;
                width: 18px;
                margin: 0;
            }

            .generate-btn mat-spinner {
                margin-right: 6px;
            }

            .close-button {
                position: absolute;
                top: 8px;
                right: 8px;
            }

            .full-width {
                width: 100%;
                margin-bottom: 8px;
            }

            .api-key-card {
                margin-bottom: 16px;
            }

            .suggestion-card {
                margin-top: 16px;
            }

            .error-card {
                margin-top: 16px;
                background-color: #ffebee;
            }

            .generated-content-editor {
                border-radius: 4px;
                overflow: hidden;
            }

            .generated-content-editor ::ng-deep .tox-tinymce {
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
            }

            .selection-mode-banner {
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 16px 20px;
                margin-bottom: 20px;
                background: linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.08) 100%);
                border: 1px solid rgba(33, 150, 243, 0.35);
                border-radius: 12px;
            }

            .selection-mode-banner .quote-icon {
                color: #64b5f6;
                font-size: 28px;
                height: 28px;
                width: 28px;
                flex-shrink: 0;
                opacity: 0.9;
            }

            .selection-mode-banner .selection-info {
                flex: 1;
                min-width: 0;
                display: flex;
                flex-direction: column;
                gap: 6px;
            }

            .selection-mode-banner .selection-header {
                font-size: 13px;
                font-weight: 600;
                color: #64b5f6;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .selection-mode-banner .selection-preview {
                font-size: 14px;
                color: rgba(255, 255, 255, 0.9);
                line-height: 1.5;
                font-style: italic;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .selection-mode-banner .clear-btn {
                flex-shrink: 0;
                opacity: 0.7;
                transition: opacity 0.2s;
            }

            .selection-mode-banner .clear-btn:hover {
                opacity: 1;
            }

            .selection-mode-banner button {
                flex-shrink: 0;
            }
        `,
    ],
})
export class PolicyAiDialogComponent implements OnInit {
    form = new FormGroup({
        apiKey: new FormControl(''), // Removed Validators.required since we handle this separately
        actionType: new FormControl('template', Validators.required),
        templateType: new FormControl('general'),
        context: new FormControl(''),
        tone: new FormControl('professional', Validators.required),
    });

    hasApiKey = false;
    isLoading = false;
    generatedSuggestion: PolicySuggestion | null = null;
    generatedHtmlContent: string | null = null;
    hasBeenRefined = false; // Track if content has been refined by AI
    errorMessage: string | null = null;
    availableTemplates: { type: string; label: string; description: string }[] = [];

    // Selection mode properties
    isSelectionMode = false;
    selectedText: string | null = null;
    fullDocument: string | null = null;

    // TinyMCE preview editor instance
    private previewEditorInstance: any = null;

    // TinyMCE configuration for preview (read-only style but editable)
    tinymcePreviewConfig: any = {
        base_url: '/tinymce',
        suffix: '.min',
        license_key: 'gpl',
        height: 450,
        menubar: false,
        skin: 'oxide-dark',
        content_css: 'dark',
        plugins: 'lists link table wordcount fullscreen',
        toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | fullscreen',
        placeholder: 'AI generated content will appear here...',
        branding: false,
        promotion: false,
        readonly: false,
    };

    private policyAiService = inject(PolicyAiService);
    private snackBar = inject(MatSnackBar);

    constructor(
        public dialogRef: MatDialogRef<PolicyAiDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PolicyAiDialogData
    ) {}

    ngOnInit(): void {
        // Initialize available templates
        this.availableTemplates = this.policyAiService.getAvailableTemplates();

        // Check if API key is already stored
        this.hasApiKey = this.policyAiService.hasApiKey();

        // Check if we're in selection mode (text was highlighted before opening dialog)
        if (this.data.selectedText && this.data.selectedText.trim().length > 0) {
            this.isSelectionMode = true;
            this.selectedText = this.data.selectedText;
            this.fullDocument = this.data.fullDocument || this.data.existingPolicy || '';
            // Default action for selection mode
            this.form.patchValue({ actionType: 'refine_selection' });
            // Show the selected text in the preview area
            this.generatedHtmlContent = this.data.selectedText;
            this.generatedSuggestion = {
                content: this.data.selectedText,
                type: 'template',
                confidence: 1.0,
            };
        }
        // If we have existing policy content (but no selection), pre-load it into the preview
        else if (this.data.existingPolicy) {
            this.form.patchValue({ actionType: 'improve' });
            // Pre-populate the generated content area with existing policy
            this.generatedHtmlContent = this.data.existingPolicy;
            // Create a mock suggestion for confidence display
            this.generatedSuggestion = {
                content: this.data.existingPolicy,
                type: 'template',
                confidence: 1.0, // 100% confidence since it's the actual content
            };
        }
    }

    /**
     * Get a preview of the selected text (truncated if too long)
     */
    getSelectionPreview(): string {
        if (!this.selectedText) return '';
        // Replace block-level tags with spaces, then strip remaining HTML
        const withSpaces = this.selectedText
            .replace(/<\/(p|div|li|h[1-6]|br|tr)>/gi, ' ') // Add space after block elements
            .replace(/<(br|hr)\s*\/?>/gi, ' ') // Add space for self-closing tags
            .replace(/<[^>]*>/g, '') // Strip remaining HTML
            .replace(/\s+/g, ' ') // Collapse multiple spaces
            .trim();
        if (withSpaces.length > 80) {
            return withSpaces.substring(0, 80) + '...';
        }
        return withSpaces;
    }

    /**
     * Clear selection mode and switch to full document refinement
     */
    clearSelection(): void {
        this.isSelectionMode = false;
        this.selectedText = null;
        this.fullDocument = null;
        // Reset to full document mode
        if (this.data.existingPolicy) {
            this.form.patchValue({ actionType: 'improve' });
            this.generatedHtmlContent = this.data.existingPolicy;
        } else {
            this.form.patchValue({ actionType: 'template' });
            this.generatedHtmlContent = null;
        }
    }

    setApiKey(): void {
        const apiKey = this.form.get('apiKey')?.value;
        if (apiKey) {
            this.policyAiService.setApiKey(apiKey);
            this.hasApiKey = true;
            this.snackBar.open('API key saved to browser storage', 'Close', { duration: 3000 });
        }
    }

    clearApiKey(): void {
        this.policyAiService.clearApiKey();
        this.hasApiKey = false;
        this.form.patchValue({ apiKey: '' });
        this.snackBar.open('API key cleared from storage', 'Close', { duration: 3000 });
    }

    generateContent(): void {
        if (!this.canGenerate()) return;

        // If API key was entered but not stored, store it automatically
        const enteredApiKey = this.form.get('apiKey')?.value?.trim();
        if (enteredApiKey && !this.hasApiKey) {
            this.policyAiService.setApiKey(enteredApiKey);
            this.hasApiKey = true;
            this.snackBar.open('API key saved to browser storage', 'Close', { duration: 2000 });
        }

        this.isLoading = true;
        this.errorMessage = null;

        // Store the previous content before clearing (for refinement)
        const previousContent = this.previewEditorInstance?.getContent() || this.generatedHtmlContent;

        this.generatedSuggestion = null;
        this.generatedHtmlContent = null;

        const actionType = this.form.get('actionType')?.value;
        const templateType = this.form.get('templateType')?.value;
        const context = this.form.get('context')?.value;
        const tone = this.form.get('tone')?.value;

        // Handle selection refinement mode
        if (this.isSelectionMode) {
            const options: PolicyPromptOptions = {
                type: 'refine_selection',
                context: context || undefined,
                selectedText: this.selectedText || undefined,
                fullDocument: this.fullDocument || undefined,
                companyName: this.data.companyName,
                tone: tone as
                    | 'formal'
                    | 'casual'
                    | 'professional'
                    | 'friendly'
                    | 'authoritative'
                    | 'empathetic'
                    | 'simple',
            };

            this.policyAiService.generatePolicyContent(options).subscribe({
                next: (suggestion) => {
                    this.generatedSuggestion = suggestion;
                    this.generatedHtmlContent = this.convertToHtml(suggestion.content);
                    this.hasBeenRefined = true;
                    this.isLoading = false;
                    this.snackBar.open('Selection refined successfully! Click "Use Content" to apply.', 'Close', {
                        duration: 3000,
                    });
                },
                error: (error) => {
                    this.errorMessage = error.message || 'Failed to refine selection';
                    this.isLoading = false;
                },
            });
            return;
        }

        // Determine which content to use as the base:
        // - If refining (action !== template and we have previous content), use previous generated content
        // - Otherwise use the original existing policy from dialog data
        const isRefining = actionType !== 'template' && previousContent;
        const policyToUse = isRefining ? previousContent : this.data.existingPolicy;

        const options: PolicyPromptOptions = {
            type: (actionType === 'template' ? templateType : actionType) as PolicyPromptOptions['type'],
            context: context || undefined,
            existingPolicy: policyToUse,
            companyName: this.data.companyName,
            tone: tone as 'formal' | 'casual' | 'professional' | 'friendly' | 'authoritative' | 'empathetic' | 'simple',
        };

        this.policyAiService.generatePolicyContent(options).subscribe({
            next: (suggestion) => {
                this.generatedSuggestion = suggestion;
                // Convert markdown to HTML for TinyMCE preview
                this.generatedHtmlContent = this.convertToHtml(suggestion.content);
                this.hasBeenRefined = true; // Mark as AI-refined
                this.isLoading = false;

                // After generating template, auto-switch to 'improve' so refinement options are ready
                if (actionType === 'template') {
                    this.form.patchValue({ actionType: 'improve' });
                    this.snackBar.open(
                        'Content generated! Select an action to refine further or click Use Content.',
                        'Close',
                        { duration: 3000 }
                    );
                } else if (isRefining) {
                    this.snackBar.open('Content refined successfully!', 'Close', { duration: 2000 });
                }
            },
            error: (error) => {
                this.errorMessage = error.message || 'Failed to generate content';
                this.isLoading = false;
                // Restore previous content on error
                if (previousContent) {
                    this.generatedHtmlContent = previousContent;
                }
            },
        });
    }

    /**
     * Check if we are refining previously generated content
     */
    isRefiningContent(): boolean {
        const actionType = this.form.get('actionType')?.value;
        return actionType !== 'template' && !!this.generatedHtmlContent;
    }

    /**
     * Called when the preview TinyMCE editor initializes
     */
    onPreviewEditorInit(event: any): void {
        this.previewEditorInstance = event.editor;

        // Apply dark theme styles
        const doc = this.previewEditorInstance.getDoc();
        if (doc && doc.body) {
            doc.body.style.backgroundColor = '#222f3e';
            doc.body.style.color = '#ffffff';
            doc.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif';
            doc.body.style.fontSize = '14px';
            doc.body.style.lineHeight = '1.6';
            doc.body.style.padding = '8px';

            const style = doc.createElement('style');
            style.textContent = `
        body { background-color: #222f3e !important; color: #ffffff !important; }
        p, div, span, li, td, th, h1, h2, h3, h4, h5, h6 { color: #ffffff !important; }
        a { color: #4fc3f7 !important; }
        ::selection { background-color: #1976d2 !important; color: #ffffff !important; }
      `;
            doc.head.appendChild(style);
        }
    }

    /**
     * Converts markdown-style text to HTML for TinyMCE
     */
    private convertToHtml(content: string): string {
        if (!content) return '';

        // Check if already HTML
        if (content.includes('<p>') || content.includes('<h1>') || content.includes('<div>')) {
            return content;
        }

        let html = content;

        // Convert headers (## Header -> <h2>Header</h2>)
        html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
        html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
        html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

        // Convert bold (**text** or __text__)
        html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

        // Convert italic (*text* or _text_)
        html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
        html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

        // Convert unordered lists (- item or * item)
        const lines = html.split('\n');
        let inList = false;
        const processedLines: string[] = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const listMatch = line.match(/^[\s]*[-*]\s+(.+)$/);

            if (listMatch) {
                if (!inList) {
                    processedLines.push('<ul>');
                    inList = true;
                }
                processedLines.push(`<li>${listMatch[1]}</li>`);
            } else {
                if (inList) {
                    processedLines.push('</ul>');
                    inList = false;
                }
                // Wrap non-empty, non-heading lines in paragraphs
                if (line.trim() && !line.startsWith('<h') && !line.startsWith('<ul') && !line.startsWith('<li')) {
                    processedLines.push(`<p>${line}</p>`);
                } else if (line.trim()) {
                    processedLines.push(line);
                }
            }
        }

        if (inList) {
            processedLines.push('</ul>');
        }

        return processedLines.join('\n');
    }

    copyToClipboard(): void {
        // Copy the HTML content from the preview editor if available
        const content = this.previewEditorInstance?.getContent() || this.generatedHtmlContent;
        if (content) {
            navigator.clipboard.writeText(content).then(() => {
                this.snackBar.open('Content copied to clipboard', 'Close', { duration: 2000 });
            });
        }
    }

    useContent(): void {
        // Get the current content from the preview editor (in case user made edits)
        const content = this.previewEditorInstance?.getContent() || this.generatedHtmlContent;
        if (content) {
            // Return a result object with the content and selection mode flag
            const result: PolicyAiDialogResult = {
                content,
                isSelectionRefinement: this.isSelectionMode,
            };
            this.dialogRef.close(result);
        }
    }

    canGenerate(): boolean {
        // Check if we have an API key (either stored or entered)
        const hasKey = this.hasApiKey || (this.form.get('apiKey')?.value?.trim() || '').length > 0;

        // Check if required form fields are valid
        const actionType = this.form.get('actionType')?.value;
        const tone = this.form.get('tone')?.value;

        return hasKey && !!actionType && !!tone;
    }
}
